/*
 * Backend-aware facade over Firebase Firestore.
 *
 * Existing modules keep their Firestore-shaped API, while this facade can route
 * document/query traffic through the company local store gateway when the
 * tenant selects Offline Server Only or Hybrid. Online Only preserves the
 * original Firebase behavior. Hybrid is local-first with explicit cloud
 * fallback controlled by Company Settings.
 */
export * from 'firebase/firestore';

import {
  addDoc as firebaseAddDoc,
  deleteDoc as firebaseDeleteDoc,
  doc as firebaseDoc,
  getDoc as firebaseGetDoc,
  getDocs as firebaseGetDocs,
  getDocFromCache,
  limit as firebaseLimit,
  onSnapshot as firebaseOnSnapshot,
  orderBy as firebaseOrderBy,
  query as firebaseQuery,
  runTransaction as firebaseRunTransaction,
  setDoc as firebaseSetDoc,
  startAfter as firebaseStartAfter,
  updateDoc as firebaseUpdateDoc,
  where as firebaseWhere,
  writeBatch as firebaseWriteBatch,
} from 'firebase/firestore';
import offlineSyncService from '../services/offlineSyncService';
import serverModeService from '../services/serverModeService';
import { recordAuditMutation } from '../services/auditTrailService';

const pathOf = (ref) => String(ref?.path || ref?._key?.path?.canonicalString?.() || '');
const collectionOf = (ref) => {
  const path = pathOf(ref);
  const parts = path.split('/').filter(Boolean);
  return parts.length >= 2 ? parts[parts.length - 2] : (parts[0] || 'Firestore');
};
const entityOf = (ref) => {
  const path = pathOf(ref);
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
};

const beginTrack = async (ref, action) => offlineSyncService.recordMutation({
  module: collectionOf(ref),
  collection: collectionOf(ref),
  action,
  entityId: entityOf(ref),
  path: pathOf(ref),
});

const auditMutation = (ref, action, data, meta = {}) => {
  Promise.resolve(recordAuditMutation({ path: pathOf(ref), action, data, meta })).catch(() => {});
};

const WRITE_ACK_GRACE_MS = 6000;

// A very small shared read cache removes duplicate Firestore reads caused by
// React StrictMode, sibling widgets and stage pages mounting at the same time.
// The TTL is deliberately short: it is a performance optimization, not an
// application data cache. Every local mutation invalidates the affected
// document + collection immediately, so existing workflow semantics remain.
const READ_CACHE_TTL_MS = 3500;
const documentReadCache = new Map();
const documentReadInFlight = new Map();
const queryReadCache = new Map();
const queryReadInFlight = new Map();

const isFirestoreTargetConflict = (error) =>
  /target id already exists/i.test(String(error?.message || error || ''));

const firebaseGetDocWithTargetRecovery = async (reference) => {
  try {
    return await firebaseGetDoc(reference);
  } catch (error) {
    if (!isFirestoreTargetConflict(error)) throw error;

    // Firestore can very occasionally surface an internal duplicate Target ID
    // while StrictMode/sibling providers are starting listeners at the same
    // time. Prefer the SDK cache first, then retry one clean network read.
    try {
      const cached = await getDocFromCache(reference);
      if (cached) return cached;
    } catch (_) {}

    await new Promise((resolve) => setTimeout(resolve, 60));
    return firebaseGetDoc(reference);
  }
};

const queryMeta = new WeakMap();
const constraintMeta = new WeakMap();

const readFresh = (cache, key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if ((Date.now() - entry.at) > READ_CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.value;
};

const stableKeyValue = (value, depth = 0) => {
  if (depth > 4) return String(value ?? '');
  if (value == null) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return `date:${value.toISOString()}`;
  if (Array.isArray(value)) return value.map((item) => stableKeyValue(item, depth + 1));
  const refPath = pathOf(value?.ref || value);
  if (refPath) return `ref:${refPath}`;
  if (typeof value?.seconds === 'number') return `ts:${value.seconds}:${value.nanoseconds || 0}`;
  if (typeof value === 'object') {
    return Object.keys(value).sort().reduce((acc, key) => {
      const current = value[key];
      if (typeof current !== 'function') acc[key] = stableKeyValue(current, depth + 1);
      return acc;
    }, {});
  }
  return String(value);
};

const invalidateReadCache = (reference) => {
  const path = queryMeta.get(reference)?.collectionPath || pathOf(reference);
  if (!path) return;
  documentReadCache.delete(`doc:${path}`);
  documentReadInFlight.delete(`doc:${path}`);

  const parts = path.split('/').filter(Boolean);
  const collectionPath = parts.length % 2 === 0 ? parts.slice(0, -1).join('/') : path;
  const prefix = `query:${collectionPath}:`;
  for (const key of queryReadCache.keys()) if (key.startsWith(prefix)) queryReadCache.delete(key);
  for (const key of queryReadInFlight.keys()) if (key.startsWith(prefix)) queryReadInFlight.delete(key);
};

const valueAt = (data, fieldPath) => String(fieldPath || '').split('.').reduce((acc, key) => acc?.[key], data);

const localDocSnapshot = (reference, data, found = true, localVersion = 0) => ({
  id: entityOf(reference),
  ref: reference,
  metadata: { fromCache: true, hasPendingWrites: false, fromLocalServer: true, localVersion: Number(localVersion || 0) },
  exists: () => Boolean(found),
  data: () => (found ? data : undefined),
  get: (fieldPath) => (found ? valueAt(data, fieldPath) : undefined),
});

const localQuerySnapshot = (reference, rows = [], changes = null) => {
  const basePath = queryMeta.get(reference)?.collectionPath || pathOf(reference);
  const docs = rows.map((row) => {
    const path = row.path || `${basePath}/${row.id}`;
    const ref = firebaseDoc(reference.firestore || reference._firestore, path);
    return localDocSnapshot(ref, row.data || row.payload || {}, row.deleted !== true, row.version || 0);
  }).filter((snap) => snap.exists());
  return {
    docs,
    size: docs.length,
    empty: docs.length === 0,
    metadata: { fromCache: true, hasPendingWrites: false, fromLocalServer: true },
    forEach: (callback, thisArg) => docs.forEach(callback, thisArg),
    docChanges: () => changes || docs.map((doc, index) => ({ type: 'added', doc, oldIndex: -1, newIndex: index })),
  };
};

const finishFirebaseWrite = async ({ ref, action, remotePromise, result }) => {
  Promise.resolve(remotePromise).catch(() => {});
  const id = await beginTrack(ref, action);
  offlineSyncService.trackRemotePromise(id, remotePromise);
  if (offlineSyncService.isOfflineLikely()) return result;

  const ack = await Promise.race([
    Promise.resolve(remotePromise).then(() => ({ ok: true })).catch((error) => ({ ok: false, error })),
    new Promise((resolve) => setTimeout(() => resolve({ queued: true }), WRITE_ACK_GRACE_MS)),
  ]);
  if (ack?.ok === false) throw ack.error;
  return result;
};

export const addDoc = async (reference, data) => {
  const ref = firebaseDoc(reference);
  invalidateReadCache(ref);
  const routed = await serverModeService.writeDocument({ path: pathOf(ref), action: 'create', data, merge: false });
  if (routed.handled) { auditMutation(ref, 'create', data); return ref; }
  const remotePromise = firebaseSetDoc(ref, data);
  auditMutation(ref, 'create', data);
  return finishFirebaseWrite({ ref, action: 'create', remotePromise, result: ref });
};

export const setDoc = async (reference, data, options) => {
  invalidateReadCache(reference);
  const routed = await serverModeService.writeDocument({
    path: pathOf(reference), action: 'set', data, merge: Boolean(options?.merge || options?.mergeFields),
  });
  if (routed.handled) { auditMutation(reference, 'set', data, { merge: Boolean(options?.merge || options?.mergeFields) }); return undefined; }
  const remotePromise = options === undefined
    ? firebaseSetDoc(reference, data)
    : firebaseSetDoc(reference, data, options);
  auditMutation(reference, 'set', data, { merge: Boolean(options?.merge || options?.mergeFields) });
  return finishFirebaseWrite({ ref: reference, action: 'set', remotePromise, result: undefined });
};

export const updateDoc = async (reference, ...args) => {
  invalidateReadCache(reference);
  let data = args[0];
  if (args.length > 1 || typeof data !== 'object' || Array.isArray(data)) {
    data = {};
    for (let i = 0; i < args.length; i += 2) data[String(args[i])] = args[i + 1];
  }
  const routed = await serverModeService.writeDocument({ path: pathOf(reference), action: 'update', data, merge: true });
  if (routed.handled) { auditMutation(reference, 'update', data); return undefined; }
  const remotePromise = firebaseUpdateDoc(reference, ...args);
  auditMutation(reference, 'update', data);
  return finishFirebaseWrite({ ref: reference, action: 'update', remotePromise, result: undefined });
};

export const deleteDoc = async (reference) => {
  invalidateReadCache(reference);
  const routed = await serverModeService.writeDocument({ path: pathOf(reference), action: 'delete', data: null });
  if (routed.handled) { auditMutation(reference, 'delete', null); return undefined; }
  const remotePromise = firebaseDeleteDoc(reference);
  auditMutation(reference, 'delete', null);
  return finishFirebaseWrite({ ref: reference, action: 'delete', remotePromise, result: undefined });
};

export const getDoc = async (reference) => {
  const path = pathOf(reference);
  if (!path) return firebaseGetDocWithTargetRecovery(reference);
  const key = `doc:${path}`;
  const cached = readFresh(documentReadCache, key);
  if (cached) return cached;
  if (documentReadInFlight.has(key)) return documentReadInFlight.get(key);

  const request = (async () => {
    const local = await serverModeService.readDocument(path);
    const snapshot = local.handled
      ? localDocSnapshot(reference, local.data, local.found, local.version || 0)
      : await firebaseGetDocWithTargetRecovery(reference);
    documentReadCache.set(key, { at: Date.now(), value: snapshot });
    return snapshot;
  })();

  documentReadInFlight.set(key, request);
  try {
    return await request;
  } finally {
    documentReadInFlight.delete(key);
  }
};

const portableFieldPath = (fieldPath) => {
  if (typeof fieldPath === 'string') return fieldPath;
  const segments = fieldPath?._internalPath?.segments || fieldPath?._delegate?._internalPath?.segments || fieldPath?._key?.path?.segments || [];
  if (Array.isArray(segments) && segments.length) return segments.join('.');
  const raw = String(fieldPath ?? '');
  if (raw.includes('__name__')) return '__name__';
  return raw;
};

export const where = (fieldPath, opStr, value) => {
  const constraint = firebaseWhere(fieldPath, opStr, value);
  constraintMeta.set(constraint, { type: 'where', field: portableFieldPath(fieldPath), op: opStr, value });
  return constraint;
};

export const orderBy = (fieldPath, directionStr = 'asc') => {
  const constraint = firebaseOrderBy(fieldPath, directionStr);
  constraintMeta.set(constraint, { type: 'orderBy', field: portableFieldPath(fieldPath), direction: directionStr || 'asc' });
  return constraint;
};

export const limit = (count) => {
  const constraint = firebaseLimit(count);
  constraintMeta.set(constraint, { type: 'limit', count: Number(count) || 0 });
  return constraint;
};

export const startAfter = (...values) => {
  const constraint = firebaseStartAfter(...values);
  const portableValues = values.map((value) => {
    if (value && typeof value === 'object' && typeof value.data === 'function' && value.id) {
      try { return { __documentSnapshot: true, id: String(value.id), data: value.data() || {} }; } catch (_) { return { __documentSnapshot: true, id: String(value.id), data: {} }; }
    }
    return value;
  });
  constraintMeta.set(constraint, { type: 'startAfter', values: portableValues });
  return constraint;
};

export const query = (reference, ...constraints) => {
  const result = firebaseQuery(reference, ...constraints);
  const base = queryMeta.get(reference);
  queryMeta.set(result, {
    collectionPath: base?.collectionPath || pathOf(reference),
    constraints: [
      ...(base?.constraints || []),
      ...constraints.map((c) => constraintMeta.get(c)).filter(Boolean),
    ],
  });
  return result;
};

const querySpec = (reference) => {
  const tagged = queryMeta.get(reference);
  const collectionPath = tagged?.collectionPath || pathOf(reference);
  if (!collectionPath) return null;
  const constraints = tagged?.constraints || [];
  // startAfter uses Firestore cursor snapshots/values that are not portable
  // across the gateway. In Hybrid we fall back to cloud; in Local-only the
  // server receives it as a best-effort cursor descriptor.
  return { collectionPath, constraints };
};

export const getDocs = async (reference) => {
  const spec = querySpec(reference);
  if (!spec?.collectionPath) return firebaseGetDocs(reference);

  let serialized = '';
  try {
    serialized = JSON.stringify(stableKeyValue(spec.constraints || []));
  } catch (_) {
    // Extremely unusual cursor/value shapes should never break a read. They
    // simply bypass the tiny query cache and use Firestore directly.
    return firebaseGetDocs(reference);
  }
  const key = `query:${spec.collectionPath}:${serialized}`;
  const cached = readFresh(queryReadCache, key);
  if (cached) return cached;
  if (queryReadInFlight.has(key)) return queryReadInFlight.get(key);

  const request = (async () => {
    const local = await serverModeService.queryDocuments(spec);
    const snapshot = local.handled
      ? localQuerySnapshot(reference, local.rows || [])
      : await firebaseGetDocs(reference);
    queryReadCache.set(key, { at: Date.now(), value: snapshot });
    return snapshot;
  })();

  queryReadInFlight.set(key, request);
  try {
    return await request;
  } finally {
    queryReadInFlight.delete(key);
  }
};

export const onSnapshot = (reference, ...args) => {
  // Support the common onSnapshot(ref, next, error) and
  // onSnapshot(ref, options, next, error) signatures.
  let options = null;
  let next;
  let error;
  if (typeof args[0] === 'function' || typeof args[0]?.next === 'function') {
    next = args[0];
    error = args[1];
  } else {
    options = args[0];
    next = args[1];
    error = args[2];
  }
  const observer = typeof next === 'object' ? next : null;
  const onNext = observer?.next?.bind(observer) || next;
  const onError = observer?.error?.bind(observer) || error;
  const deliverSnapshot = (snapshot) => {
    // A realtime event is authoritative and must invalidate the short read
    // cache before page callbacks re-query through their normal services.
    invalidateReadCache(reference);
    onNext?.(snapshot);
  };
  const spec = querySpec(reference);
  const isDoc = pathOf(reference).split('/').filter(Boolean).length % 2 === 0 && !spec?.constraints?.length;
  let cancelled = false;
  let cloudUnsubscribe = null;
  let timer = null;
  let previous = new Map();

  const startCloud = () => {
    if (cancelled || cloudUnsubscribe) return;
    cloudUnsubscribe = options == null
      ? firebaseOnSnapshot(reference, deliverSnapshot, onError)
      : firebaseOnSnapshot(reference, options, deliverSnapshot, onError);
  };

  const poll = async () => {
    if (cancelled) return;
    try {
      if (isDoc) {
        const local = await serverModeService.readDocument(pathOf(reference));
        if (!local.handled) { startCloud(); return; }
        deliverSnapshot(localDocSnapshot(reference, local.data, local.found));
      } else if (spec) {
        const local = await serverModeService.queryDocuments(spec);
        if (!local.handled) { startCloud(); return; }
        const rows = local.rows || [];
        const current = new Map(rows.map((row) => [String(row.id || row.path), JSON.stringify(row.data || row.payload || {})]));
        const changes = [];
        const snapshot = localQuerySnapshot(reference, rows);
        snapshot.docs.forEach((doc, index) => {
          const key = doc.id;
          if (!previous.has(key)) changes.push({ type: 'added', doc, oldIndex: -1, newIndex: index });
          else if (previous.get(key) !== current.get(key)) changes.push({ type: 'modified', doc, oldIndex: index, newIndex: index });
        });
        previous.forEach((_, key) => {
          if (!current.has(key)) {
            const ref = firebaseDoc(reference.firestore || reference._firestore, `${spec.collectionPath}/${key}`);
            changes.push({ type: 'removed', doc: localDocSnapshot(ref, {}, false), oldIndex: -1, newIndex: -1 });
          }
        });
        previous = current;
        deliverSnapshot({ ...snapshot, docChanges: () => changes });
      } else {
        startCloud();
        return;
      }
    } catch (err) {
      onError?.(err);
    }
    if (!cancelled && !cloudUnsubscribe) timer = setTimeout(poll, 2500);
  };

  poll();
  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    if (cloudUnsubscribe) cloudUnsubscribe();
  };
};

export const writeBatch = (firestore) => {
  const batch = firebaseWriteBatch(firestore);
  const touched = [];
  const facade = {
    set(reference, data, options) {
      touched.push({ ref: reference, path: pathOf(reference), action: 'set', data, merge: Boolean(options?.merge || options?.mergeFields) });
      if (options === undefined) batch.set(reference, data);
      else batch.set(reference, data, options);
      return facade;
    },
    update(reference, ...args) {
      let data = args[0];
      if (args.length > 1 || typeof data !== 'object' || Array.isArray(data)) {
        data = {};
        for (let i = 0; i < args.length; i += 2) data[String(args[i])] = args[i + 1];
      }
      touched.push({ ref: reference, path: pathOf(reference), action: 'update', data, merge: true });
      batch.update(reference, ...args);
      return facade;
    },
    delete(reference) {
      touched.push({ ref: reference, path: pathOf(reference), action: 'delete', data: null });
      batch.delete(reference);
      return facade;
    },
    async commit() {
      touched.forEach(({ ref }) => invalidateReadCache(ref));
      const routed = await serverModeService.batchWrite(touched.map(({ path, action, data, merge }) => ({ path, action, data, merge })));
      if (routed.handled) { touched.forEach(({ ref, action, data }) => auditMutation(ref, `batch_${action}`, data)); return []; }
      const remotePromise = batch.commit();
      touched.forEach(({ ref, action, data }) => auditMutation(ref, `batch_${action}`, data));
      Promise.resolve(remotePromise).catch(() => {});
      const journalIds = await Promise.all(touched.map(({ ref, action }) => beginTrack(ref, `batch_${action}`)));
      journalIds.forEach((id) => offlineSyncService.trackRemotePromise(id, remotePromise));
      if (offlineSyncService.isOfflineLikely()) return [];
      const ack = await Promise.race([
        Promise.resolve(remotePromise).then(() => ({ ok: true })).catch((err) => ({ ok: false, error: err })),
        new Promise((resolve) => setTimeout(() => resolve({ queued: true }), WRITE_ACK_GRACE_MS)),
      ]);
      if (ack?.ok === false) throw ack.error;
      return [];
    },
  };
  return facade;
};

const cachedSnapshot = async (reference) => {
  try { return await getDocFromCache(reference); }
  catch (_) { return firebaseGetDoc(reference); }
};

export const runTransaction = async (firestore, updateFunction, options) => {
  const infrastructure = serverModeService.getSnapshot();
  const serverMode = infrastructure.effectiveMode;
  const localMode =
    serverMode === 'offline_server_only' ||
    infrastructure.localRoutingEnabled === true;
  if (!offlineSyncService.isOfflineLikely() && !localMode) {
    // Keep native Firestore transaction guarantees, but proxy mutations so the
    // tiny shared read cache cannot serve a pre-transaction snapshot afterward.
    return firebaseRunTransaction(firestore, async (transaction) => {
      const facade = {
        get: (reference) => transaction.get(reference),
        set(reference, data, setOptions) {
          invalidateReadCache(reference);
          if (setOptions === undefined) transaction.set(reference, data);
          else transaction.set(reference, data, setOptions);
          return facade;
        },
        update(reference, ...args) {
          invalidateReadCache(reference);
          transaction.update(reference, ...args);
          return facade;
        },
        delete(reference) {
          invalidateReadCache(reference);
          transaction.delete(reference);
          return facade;
        },
      };
      return updateFunction(facade);
    }, options);
  }

  // Local-server transactions use optimistic version preconditions. Reads are
  // recorded with the LAN document version and the server validates all of
  // them inside one SQLite BEGIN IMMEDIATE transaction before applying writes.
  // A concurrent LAN change retries the user's transaction function, matching
  // Firestore's retry semantics without altering business-level transaction code.
  if (localMode) {
    const maxAttempts = Math.max(1, Number(options?.maxAttempts || 5));
    let lastConflict = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const operations = [];
      const preconditions = new Map();
      const localTx = {
        async get(reference) {
          const routed = await serverModeService.readDocument(pathOf(reference));
          if (!routed.handled) return cachedSnapshot(reference);
          preconditions.set(pathOf(reference), Number(routed.version || 0));
          return localDocSnapshot(reference, routed.data, routed.found, routed.version || 0);
        },
        set(reference, data, setOptions) {
          operations.push({ type: 'set', reference, data, options: setOptions });
          return localTx;
        },
        update(reference, ...args) {
          operations.push({ type: 'update', reference, args });
          return localTx;
        },
        delete(reference) {
          operations.push({ type: 'delete', reference });
          return localTx;
        },
      };

      const result = await updateFunction(localTx);
      const routedOps = operations.map((op) => {
        invalidateReadCache(op.reference);
        if (op.type === 'set') return { path: pathOf(op.reference), action: 'set', data: op.data, merge: Boolean(op.options?.merge || op.options?.mergeFields), ref: op.reference };
        if (op.type === 'delete') return { path: pathOf(op.reference), action: 'delete', data: null, merge: false, ref: op.reference };
        let data = op.args[0];
        if (op.args.length > 1 || typeof data !== 'object' || Array.isArray(data)) {
          data = {};
          for (let i = 0; i < op.args.length; i += 2) data[String(op.args[i])] = op.args[i + 1];
        }
        return { path: pathOf(op.reference), action: 'update', data, merge: true, ref: op.reference };
      });

      try {
        const routed = await serverModeService.batchWrite(
          routedOps.map(({ ref, ...op }) => op),
          { preconditions: Array.from(preconditions, ([path, version]) => ({ path, version })) }
        );
        if (!routed.handled) break;
        routedOps.forEach(({ ref, action, data }) => auditMutation(ref, `transaction_${action}`, data));
        return result;
      } catch (error) {
        if (error?.code !== 'transaction-conflict' || attempt + 1 >= maxAttempts) throw error;
        lastConflict = error;
        await new Promise((resolve) => setTimeout(resolve, 15 + attempt * 20));
      }
    }
    if (lastConflict) throw lastConflict;
  }

  // Firebase-only offline cache fallback preserves the pre-existing behavior.
  const operations = [];
  const offlineTx = {
    async get(reference) { return cachedSnapshot(reference); },
    set(reference, data, setOptions) { operations.push({ type: 'set', reference, data, options: setOptions }); return offlineTx; },
    update(reference, ...args) { operations.push({ type: 'update', reference, args }); return offlineTx; },
    delete(reference) { operations.push({ type: 'delete', reference }); return offlineTx; },
  };
  const result = await updateFunction(offlineTx);
  const batch = writeBatch(firestore);
  operations.forEach((op) => {
    if (op.type === 'set') { if (op.options === undefined) batch.set(op.reference, op.data); else batch.set(op.reference, op.data, op.options); }
    else if (op.type === 'update') batch.update(op.reference, ...op.args);
    else if (op.type === 'delete') batch.delete(op.reference);
  });
  await batch.commit();
  return result;
};

export const rawAddDoc = firebaseAddDoc;
